import React from 'react';
import {
  Id,
  toast,
  ToastContainer,
  ToastContent,
  ToastOptions,
  TypeOptions,
} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './overrides.scss';
import { Icon } from '../icon';

type Props = { children?: React.ReactNode };
function ToastProvider({ children }: Props) {
  return (
    <>
      {children}
      <ToastContainer icon={false} position={'bottom-right'}></ToastContainer>
    </>
  );
}

const ToastContentWrapper = <TData,>({
  content,
  id,
}: {
  content: ToastContent<TData>;
  id?: string;
}) => <div data-cy={id}>{content as React.ReactNode}</div>;

type ToastrOptions<TData = unknown> = Omit<
  ToastOptions<TData>,
  'icon' | 'theme' | 'position'
> & {
  id?: string;
};

type ToastrFn = <TData = unknown>(
  content: ToastContent<TData>,
  options?: ToastrOptions<TData>,
) => Id;

export const notify: Record<TypeOptions, ToastrFn> = {
  default: (message, options) => {
    const { id, ...toastOptions } = options || {};
    return toast.success(
      <ToastContentWrapper content={message} id={id} />,
      toastOptions,
    );
  },
  success: (message, options) => {
    const { id, ...toastOptions } = options || {};
    return toast.success(<ToastContentWrapper content={message} id={id} />, {
      ...toastOptions,
      icon: <Icon icon='check-box-circle' className='text-success-text' />,
      theme: 'colored',
    });
  },
  warning: (message, options) => {
    const { id, ...toastOptions } = options || {};
    return toast.warn(<ToastContentWrapper content={message} id={id} />, {
      ...toastOptions,
      icon: <Icon icon='alert' className='text-warning-text' />,
      theme: 'colored',
    });
  },
  error: (message, options) => {
    const { id, ...toastOptions } = options || {};
    return toast.error(<ToastContentWrapper content={message} id={id} />, {
      ...toastOptions,
      icon: <Icon icon='alert' className='text-error-text' />,
      theme: 'colored',
    });
  },
  info: (message, options) => {
    const { id, ...toastOptions } = options || {};
    return toast.info(<ToastContentWrapper content={message} id={id} />, {
      ...toastOptions,
      icon: <Icon icon='info-circle' className='text-info-text' />,
      theme: 'colored',
    });
  },
};

export default ToastProvider;
